import { Box, Collapse, useDisclosure } from '@chakra-ui/react';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { Button, IconButton } from '@main/complykit';
import { Integration_Auth_Names_Enum, Integration_Names_Enum } from '@main/graphql/types.generated';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod/v4';

import {
  ConnectorConfigForm,
  type ConnectorConfigSubmit,
  type ConnectorConfigSubmitResult,
} from '../connector-config-form';
import { hasConfigurableAuthSchemas } from './app-connector-card-config';
import { AppConnectorCardFrame } from './app-connector-card-frame';

export type ConnectorConnectResult = {
  status: 'connected' | 'cancelled' | 'failed';
};

export type DisconnectedAppConnectorCardProps = {
  providerName: Integration_Names_Enum;
  connectorName?: string;
  authName: Integration_Auth_Names_Enum;
  configSchema?: z.ZodObject;
  encryptedConfigSchema?: z.ZodObject;
  onConnect?: (config?: ConnectorConfigSubmit) => Promise<ConnectorConnectResult>;
};

export const DisconnectedAppConnectorCard = ({
  providerName,
  connectorName,
  authName,
  configSchema,
  encryptedConfigSchema,
  onConnect,
}: DisconnectedAppConnectorCardProps) => {
  const { t } = useTranslation('integrations');
  const configCollapse = useDisclosure();
  const [isConnecting, setIsConnecting] = useState(false);
  const isConfigurable = hasConfigurableAuthSchemas({ configSchema, encryptedConfigSchema });

  const toggleForm = () => {
    if (configCollapse.isOpen) {
      configCollapse.onClose();
      return;
    }

    configCollapse.onOpen();
  };

  const submitConnect = async (
    config?: ConnectorConfigSubmit,
  ): Promise<ConnectorConfigSubmitResult> => {
    if (!onConnect) {
      throw new Error('Connector connect handler is not available.');
    }

    setIsConnecting(true);
    try {
      const result = await onConnect(config);
      if (result.status === 'connected') {
        configCollapse.onClose();
        return { status: 'committed' };
      }

      return { status: 'notCommitted', reason: result.status };
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <AppConnectorCardFrame
      providerName={providerName}
      connectorName={connectorName}
      actions={
        isConfigurable ? (
          <IconButton
            label={t('configureForm.configureBtn')}
            icon={Cog6ToothIcon}
            variant="soft"
            isPending={isConnecting}
            onPress={toggleForm}
          />
        ) : (
          onConnect && (
            <Button
              isPending={isConnecting}
              onPress={() => {
                void submitConnect().catch(() => undefined);
              }}
            >
              {t('connectBtn')}
            </Button>
          )
        )
      }
    >
      {isConfigurable && (
        <Collapse in={configCollapse.isOpen} unmountOnExit>
          <Box mt={8}>
            <ConnectorConfigForm
              t={t}
              tKey={`integrations.${providerName}.connectors.${authName}.config`}
              configSchema={configSchema ?? z.object({})}
              encryptedConfigSchema={encryptedConfigSchema ?? z.object({})}
              encryptedFields={[]}
              submitLabel={t('connectBtn')}
              requireDirtyToSubmit={false}
              onCancel={configCollapse.onClose}
              onSubmit={submitConnect}
            />
          </Box>
        </Collapse>
      )}
    </AppConnectorCardFrame>
  );
};
